/**
 * R6 §4.2: the app set, fetched once per identity, and again only when a consumer calls `reload`.
 *
 * Mirrors `grants-state.ts`'s three-state discipline (D-13), for the same reason and with the
 * same trap: `x: null` + `error: null` = unknown; `x: null` + `error` = errorstate;
 * `x: []` = loaded-none.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { listApps, type App } from '../api/apps.api';
import { displayMessage } from '../platform/errors';
import { ApiError } from '../platform/http/client';

/** The app list as the console last read it, with no control. `visibleAppsState` returns it. */
export interface AppsListState {
  /**
   * ⛔ `null` = not yet known OR unusable. NEVER "none" — `[]` is "none".
   *
   * Never collapse this to `[]` before the landing decision: `[]` routes to the zero-access
   * panel, so telling a principal they have no apps because a fetch is in flight is exactly
   * the `absent → denied` collapse this program exists to delete.
   */
  readonly apps: readonly App[] | null;
  /**
   * Non-null iff `apps` is null for a REASON rather than because it has not arrived.
   * `status` is the HTTP status of the failed read, or `null` when no response came.
   */
  readonly error: { readonly message: string; readonly status: number | null } | null;
}

/** What {@link useAppsContext} returns: the list state and the control that reads it again. */
export interface AppsState extends AppsListState {
  /**
   * Asks for the list again. A usable stored list stays in `apps`, with `rereadPending` true, until
   * the new answer lands. A stored failure turns unknown (`apps: null`, `error: null`), so a retried
   * failure never shows the old reason as the new one.
   */
  readonly reload: () => void;
  /**
   * `true` while a `reload` runs. The list in `apps` is then the previous usable answer for this
   * identity, which can lack an app the server just made, so an id absent from it decides nothing.
   */
  readonly rereadPending: boolean;
  /**
   * The failure of the last re-read, when `apps` is the previous usable list for this identity.
   * `null` while a re-read runs and after every other answer. An id absent from `apps` then decides
   * nothing: the failed re-read can be the one that carried an app the server just made.
   */
  readonly rereadError: AppsListState['error'];
}

const APPS_UNKNOWN: AppsListState = { apps: null, error: null };
/** Signed out: nothing to ask for, so this is loaded-none rather than unknown. */
const APPS_NONE: AppsListState = { apps: [], error: null };

/**
 * A fetched `AppsListState`, TAGGED with the identity it was fetched FOR.
 *
 * The tag is the whole point, and it is the same argument `grants-state.ts` makes: `null` is
 * not enough to mean "not known yet", because a list fetched for operator A is equally
 * not-known for operator B — and B's is the answer being rendered. An untagged store would
 * show the PREVIOUS operator's apps for one commit after an identity change, because React
 * flushes passive effects AFTER the commit.
 */
export interface FetchedApps {
  readonly forOperatorId: string | null;
  readonly state: AppsListState;
  /** The failure of the last re-read when `state` is the previous usable list, else `null`. */
  readonly rereadError: AppsListState['error'];
}

/**
 * Which `AppsListState` may be SHOWN. A RENDER-TIME derivation, and it must stay one — the
 * equivalent "reset the stored state from an effect" shape always leaves one commit holding
 * the previous value.
 *
 * Returns the module constants by reference on every not-showable path, so a consumer
 * memoising on identity does not churn.
 */
export function visibleAppsState(
  isAuthenticated: boolean,
  operatorId: string | null,
  fetched: FetchedApps | null,
): AppsListState {
  if (!isAuthenticated) return APPS_NONE;
  if (fetched === null || fetched.forOperatorId !== operatorId) return APPS_UNKNOWN;
  return fetched.state;
}

/**
 * Store one app list answer, given the answer stored before it.
 *
 * A failed read for the same identity keeps the last usable list. The re-read follows a write such
 * as `POST /v1/apps`, and one transient failure there must not drop every app the console already
 * showed. Every other answer replaces the stored one: the first answer for an identity, an answer
 * for another identity, and every usable list.
 */
export function settleFetchedApps(
  previous: FetchedApps | null,
  forOperatorId: string | null,
  next: AppsListState,
): FetchedApps {
  if (next.apps === null && previous !== null && previous.forOperatorId === forOperatorId && previous.state.apps !== null) {
    return { forOperatorId, state: previous.state, rereadError: next.error };
  }
  return { forOperatorId, state: next, rereadError: null };
}

/**
 * Replace ONE app in the fetched list, in place.
 *
 * ⛔ A REPLACE keyed on `app.id`, not a refetch and not an append. A refetch would re-issue
 * `GET /v1/apps` and re-run the landing decision mid-session; an append would duplicate the row.
 * An id that is not in the list is a NO-OP: the caller edited an app the list never carried, which
 * is reachable (a grant revoked between load and save) and must not invent a row.
 *
 * ⛔ It does NOT touch `error`, and it does NOT run when `apps === null`. `null` is "not known
 * yet" (this file's own D-13 rule); writing a one-element list into it would turn "unknown" into
 * "you have exactly one app", which routes the landing decision.
 */
export function replaceAppInState(state: AppsListState, next: App): AppsListState {
  if (state.apps === null) return state;
  if (!state.apps.some((a) => a.id === next.id)) return state;
  return { ...state, apps: state.apps.map((a) => (a.id === next.id ? next : a)) };
}

const AppsCtx = createContext<AppsState | null>(null);

/**
 * The mutator, in its OWN context.
 *
 * ⛔ Separate from {@link AppsCtx} deliberately: a consumer that only reads the list must not
 * re-render when the mutation object's provider re-renders, and a consumer that only mutates must
 * not re-render when the list changes. One combined context would couple both directions.
 */
export interface AppsMutation {
  /**
   * Apply a server-confirmed app row to the session's list.
   *
   * ⛔ Call with the PATCH RESPONSE, never with the local draft — the server owns `updatedAt` and
   * may normalise `description`. Writing the draft would put a value on screen that the database
   * does not hold.
   */
  readonly replaceApp: (app: App) => void;
}

const AppsMutationCtx = createContext<AppsMutation | null>(null);

export function useAppsMutation(): AppsMutation {
  const ctx = useContext(AppsMutationCtx);
  if (ctx === null) throw new Error('useAppsMutation must be used within an AppsProvider');
  return ctx;
}

export interface AppsProviderProps {
  readonly isAuthenticated: boolean;
  readonly operatorId: string | null;
  readonly children: ReactNode;
}

export function AppsProvider({
  isAuthenticated,
  operatorId,
  children,
}: AppsProviderProps): React.JSX.Element {
  const [fetched, setFetched] = useState<FetchedApps | null>(null);
  /* Each reload bumps this, and the fetch effect runs again. */
  const [attempt, setAttempt] = useState(0);
  /* The attempt of the last read that landed. A higher `attempt` is a re-read in flight. */
  const [settledAttempt, setSettledAttempt] = useState(0);
  const rereadPending = isAuthenticated && attempt !== settledAttempt;
  const stored = visibleAppsState(isAuthenticated, operatorId, fetched);
  /* A re-read of a failed answer shows unknown, so the old reason never reads as the new one. */
  const visible = rereadPending && stored.apps === null ? APPS_UNKNOWN : stored;
  /* Shown only beside the list it qualifies, and never while the next re-read runs. */
  const storedRereadError = fetched !== null && stored === fetched.state ? fetched.rereadError : null;
  const rereadError = rereadPending ? null : storedRereadError;
  /*
   * Identity-stable: the setter carries the previous value, so nothing is captured. The stored
   * answer stays until the new one lands, so the shell keeps its landing and its rail.
   */
  const reload = useCallback(() => {
    setAttempt((n) => n + 1);
  }, []);
  const state = useMemo<AppsState>(
    () => ({ ...visible, reload, rereadPending, rereadError }),
    [visible, reload, rereadPending, rereadError],
  );

  /**
   * ⛔ IDENTITY-STABLE — empty dep array, and the setter's callback form is what makes that safe
   * (it carries the previous value, so nothing needs to be captured). An unstable mutator would
   * re-render every consumer of {@link AppsMutationCtx} on each provider render, which is the cost
   * the second context exists to avoid.
   *
   * ⚠️ `prev === null` is passed through untouched: replacing into a list that has not arrived
   * would fabricate a `FetchedApps` with no identity tag, and the tag is what stops one operator's
   * list being shown to another.
   */
  const replaceApp = useCallback((app: App) => {
    setFetched((prev) =>
      prev === null ? prev : { ...prev, state: replaceAppInState(prev.state, app) },
    );
  }, []);
  const mutation = useMemo<AppsMutation>(() => ({ replaceApp }), [replaceApp]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    let live = true;
    // Captured, NOT read inside the callback: the tag must be the identity the request was
    // issued FOR, not whoever is signed in when it lands.
    const forOperatorId = operatorId;
    const settle = (next: AppsListState): void => {
      if (!live) return;
      setFetched((previous) => settleFetchedApps(previous, forOperatorId, next));
      setSettledAttempt(attempt);
    };
    listApps()
      .then((apps) => settle({ apps, error: null }))
      .catch((e: unknown) => {
        // ⛔ Never `[]` here. `[]` means "you have no apps" and would route the operator to
        // the zero-access panel — a denial invented from a failed request.
        settle({
          apps: null,
          error: {
            message: displayMessage(e, 'Could not load your apps.'),
            status: e instanceof ApiError ? e.status : null,
          },
        });
      });
    return () => {
      live = false;
    };
    // Re-fetch when the identity changes: a different operator has a different list, and
    // reusing the previous one would be an authorization-visibility bug.
  }, [isAuthenticated, operatorId, attempt]);

  // ⛔ `AppScopeProvider` is deliberately NOT changed. `AppScopeRoute` resolves the scoped app
  // from this list, so replacing the row re-renders the scope with the new value for free. A
  // second mutation path there would be two sources of truth for one app row.
  return (
    <AppsMutationCtx value={mutation}>
      <AppsCtx value={state}>{children}</AppsCtx>
    </AppsMutationCtx>
  );
}

export function useAppsContext(): AppsState {
  const ctx = useContext(AppsCtx);
  if (ctx === null) throw new Error('useAppsContext must be used within an AppsProvider');
  return ctx;
}

/**
 * The app list state, or `null` outside an `AppsProvider`.
 *
 * Consumer: `AppScopeRoute`, which renders under `CpAdminApp`'s provider in the console and with no
 * provider in the route-table tests. It names a failed read and offers `reload` where the list is
 * unknown. `null` means that no app list state reaches this tree, so no control there can read it.
 */
export function useOptionalAppsContext(): AppsState | null {
  return useContext(AppsCtx);
}

// No polling and no websocket: the chrome never changes shape on its own mid-session. The list
// is read once per identity, and again on `reload`: a retry a person presses, or the re-read that
// follows a write such as `POST /v1/apps`. Another grant added mid-session appears on the next load.
